import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, ExternalLink, MapPin, Clock } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminScheduleEvent } from '@/lib/api'
import { useConfirm } from '@/components/ConfirmDialog'

const BADGE_COLOR: Record<string, string> = {
  HANDSHAKE:    'bg-blue-500/15 text-blue-300 border border-blue-500/20',
  CONCERT:      'bg-purple-500/15 text-purple-300 border border-purple-500/20',
  'FAN MEETING':'bg-green-500/15 text-green-300 border border-green-500/20',
}

function formatDate(d: string | null) {
  if (!d) return 'TBA'
  try {
    return new Date(d).toLocaleDateString('id-ID', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })
  } catch { return d }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

export default function ScheduleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()
  const { confirm, dialog } = useConfirm()

  const [event, setEvent] = useState<AdminScheduleEvent | null>(
    (location.state as { event?: AdminScheduleEvent } | null)?.event ?? null
  )
  const [loading, setLoading] = useState(!event)

  useEffect(() => {
    if (event || !token || !id) return
    api.getAdminSchedule(token)
      .then((items) => setEvent(items.find((e) => e.id === id) ?? null))
      .finally(() => setLoading(false))
  }, [event, id, token])

  const handleDelete = async () => {
    if (!event) return
    if (!await confirm(
      `"${event.name}" akan dihapus permanen dan tidak bisa dipulihkan.`,
      { title: `Hapus "${event.name}"` }
    )) return
    if (!token) return
    try {
      await api.deleteSchedule(token, event.id)
      navigate(-1)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (!event) {
    return <div className="p-6"><p className="text-red-400 text-sm">Event not found.</p></div>
  }

  return (
    <div className="p-6">
      {/* Nav */}
      <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <ChevronLeft size={14} />
          Kembali
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/schedule/${event.id}/edit`, { state: { event } })}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="text-xs text-red-400 hover:text-red-300 transition-colors px-3 py-2 rounded hover:bg-zinc-800"
          >
            Hapus
          </button>
        </div>
      </div>

      <div className="max-w-xl mx-auto">
      {/* Hero card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 mb-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${BADGE_COLOR[event.badge] ?? 'bg-zinc-800 text-zinc-300 border border-zinc-700'}`}>
            {event.badge}
          </span>
          <span className="text-[11px] text-zinc-500">{event.group_slug}</span>
        </div>

        <h1 className="text-2xl font-bold text-white leading-tight">{event.name}</h1>

        <div className="space-y-2.5 pt-1">
          <div className="flex items-center gap-2.5 text-sm text-zinc-300">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <div className="w-3 h-3 rounded-sm border border-zinc-500" />
            </div>
            <span>{formatDate(event.date)}</span>
            {event.time && (
              <>
                <span className="text-zinc-700">·</span>
                <Clock size={13} className="text-zinc-500 flex-shrink-0" />
                <span>{event.time}</span>
              </>
            )}
          </div>
          {event.location && (
            <div className="flex items-center gap-2.5 text-sm text-zinc-300">
              <MapPin size={13} className="text-zinc-500 flex-shrink-0" />
              <span>{event.location}</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {/* Cheki link */}
        {event.cheki_url && (
          <a
            href={event.cheki_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-xl px-5 py-4 transition-colors group"
          >
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">Beli Tiket / Cheki</p>
              <p className="text-sm font-medium text-white group-hover:text-yellow-400 transition-colors truncate max-w-xs">
                {event.cheki_url}
              </p>
            </div>
            <ExternalLink size={14} className="text-zinc-600 group-hover:text-yellow-400 transition-colors flex-shrink-0" />
          </a>
        )}

        {/* Meta */}
        <Section title="Info Teknis">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Event ID</p>
              <p className="text-xs text-zinc-600 font-mono break-all">{event.id}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Group</p>
              <p className="text-xs text-zinc-400 font-mono">{event.group_slug}</p>
            </div>
          </div>
        </Section>
      </div>

      </div>
      {dialog}
    </div>
  )
}
